import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  FilterFn,
} from "@tanstack/react-table";
import { useAuth } from "../AuthContext";
import { ALL_CUSTOMERS_QUERY } from "../graphql/customers";
import Table from "../components/Table";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AddIcon from "@mui/icons-material/Add";
import "./Customers.css";
import CustomerDialog from "../components/CustomerDialog";

interface Address {
  id: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  addressType: string;
}

interface Customer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
  archivedAt: string | null;
  addressesByCustomerId: {
    nodes: Address[];
  };
}

const globalFilterFn: FilterFn<Customer> = (row, columnId, filterValue) => {
  if (!filterValue) return true;
  const searchLower = String(filterValue).toLowerCase();
  return (
    row.original.name.toLowerCase().includes(searchLower) ||
    (row.original.email?.toLowerCase().includes(searchLower) ?? false) ||
    (row.original.phone?.toLowerCase().includes(searchLower) ?? false)
  );
};

const columnHelper = createColumnHelper<Customer>();

const columns = [
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <a
        href={`/customers/${info.row.original.id}`}
        style={{
          color: "#3b82f6",
          textDecoration: "none",
          fontWeight: 600,
        }}
      >
        {info.getValue()}
      </a>
    ),
    size: 200,
  }),
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => info.getValue() || "—",
    size: 200,
  }),
  columnHelper.accessor("phone", {
    header: "Phone",
    cell: (info) => info.getValue() || "—",
    size: 150,
  }),
  columnHelper.accessor((row) => row.addressesByCustomerId.nodes.length, {
    id: "addressCount",
    header: "Addresses",
    cell: (info) => {
      const count = info.getValue();
      return (
        <span style={{ fontWeight: 600, color: "#6b7280" }}>
          {count} {count === 1 ? "address" : "addresses"}
        </span>
      );
    },
    size: 120,
  }),
  columnHelper.accessor("createdAt", {
    header: "Created",
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    size: 120,
  }),
];

export default function Customers() {
  const [showDialog, setShowDialog] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const { user } = useAuth();


  const { loading, error, refetch } = useQuery(ALL_CUSTOMERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    onCompleted: (responseData) => {
      const filtered = (responseData?.allCustomers?.nodes || []).filter(
        (c: Customer) => !c.archivedAt
      );
      setCustomers(filtered);
    },
  });

  // Apollo doesn't reliably re-fire useQuery's onCompleted callback on a
  // refetch() call, so setCustomers must be driven from refetch()'s own
  // resolved data rather than assuming onCompleted will run again.
  const refreshCustomers = async () => {
    const { data: responseData } = await refetch();
    const filtered = (responseData?.allCustomers?.nodes || []).filter(
      (c: Customer) => !c.archivedAt
    );
    setCustomers(filtered);
  };

  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <div className="customers-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Customers</h1>
          <p className="subtitle">Manage your customers and addresses</p>
        </div>
        <button
          className="primary-button btn-icon"
          onClick={() => setShowDialog(true)}
          disabled={loading}
        >
          <AddIcon fontSize="small" /> New Customer
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error.message}
          </div>
        )}

        {loading && !customers.length && (
          <div className="loading-message">Loading customers...</div>
        )}

        {!loading && customers.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon"><PeopleOutlinedIcon fontSize="inherit" /></div>
            <h2>No customers yet</h2>
            <p>Create your first customer from the Customer Detail page</p>
          </div>
        )}

        {customers.length > 0 && (
          <>
            <div className="filter-bar">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name, email, or phone..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
            <Table table={table} isLoading={loading} />
          </>
        )}
      </div>
      {showDialog && (
        <CustomerDialog onClose={() => setShowDialog(false)} onCreated={refreshCustomers} />
      )}
    </div>
  );
}
